import React, {
  useCallback, useEffect, useMemo, useState, type RefObject,
} from 'react';
import {
  isTrackReference,
  useLocalParticipant,
  useParticipants,
  useTracks,
  type TrackReferenceOrPlaceholder,
} from '@livekit/components-react';
import { Track, type Participant } from 'livekit-client';
import * as Clipboard from 'expo-clipboard';

import { attachPreviewStream } from '@/common/components/meeting/attachPreviewStream';
import { MeetingRoom } from '@/common/components/meeting/MeetingRoom';
import { ParticipantTile } from '@/common/components/meeting/MeetingVideoTiles';
import type { MeetingJoinSettings } from '@/common/components/meeting/types';
import { isLecturerParticipant } from '@/common/components/oralExam/BriefingConferenceStage';
import { useTranslation } from 'react-i18next';

import {
  LabStandInImage,
  useOralExamLabOverrides,
} from '@/common/oralExam/oralExamLabOverrides';

import {
  PreflightSupervisorTiles,
  PreflightSupervisorsColumnLayout,
} from './PreflightSupervisorsColumnLayout';

export type PreflightSupervisorsColumnProps = {
  slotId: string;
  joinSettings: MeetingJoinSettings;
  joinCreds: { token: string; livekitUrl: string; roomName?: string } | null;
  /** Ends cohort-token quiet remint wait once LiveKit connects. */
  onRoomTokenConnected?: () => void;
  /** Receives the local lecturer camera element once the column track is sending. */
  localCameraVideoRef?: RefObject<HTMLVideoElement | null>;
  /** Fires when the hidden capture video is bound to the local camera track. */
  onLocalCameraVideoReady?: () => void;
};

/** Hidden capture surface bound to the local LiveKit camera for avatar stills. */
export function PreflightLocalCameraCapture({
  captureVideoRef,
  onCaptureVideoReady,
}: {
  captureVideoRef: RefObject<HTMLVideoElement | null>;
  onCaptureVideoReady?: () => void;
}) {
  const { localParticipant } = useLocalParticipant();
  const cameraPublication = localParticipant.getTrackPublication(Track.Source.Camera);
  const cameraTrackSid = cameraPublication?.trackSid ?? null;

  useEffect(() => {
    const video = captureVideoRef.current;
    const mediaTrack = cameraPublication?.track?.mediaStreamTrack;
    if (!video || !mediaTrack || mediaTrack.readyState === 'ended') {
      if (video) {
        video.srcObject = null;
      }
      return undefined;
    }
    attachPreviewStream(video, new MediaStream([mediaTrack]));
    onCaptureVideoReady?.();
    return () => {
      video.srcObject = null;
    };
  }, [cameraPublication?.track, cameraTrackSid, captureVideoRef, onCaptureVideoReady]);

  return (
    <video
      ref={captureVideoRef}
      muted
      playsInline
      style={{
        position: 'absolute',
        width: 1,
        height: 1,
        opacity: 0,
        pointerEvents: 'none',
      }}
      data-testid="preflight-local-camera-capture"
    />
  );
}

/**
 * A subscribed camera list hides a lecturer who is in the room but has not
 * published a camera yet — including the person looking at this column.
 * Build cards from room membership, and attach a camera track when one exists.
 */
function supervisorCameraTiles(
  cameraTracks: TrackReferenceOrPlaceholder[],
  participants: Participant[],
  localParticipant: Participant,
): TrackReferenceOrPlaceholder[] {
  const people = new Map<string, Participant>();
  const add = (participant: Participant | undefined) => {
    if (participant && isLecturerParticipant(participant)) {
      people.set(participant.identity, participant);
    }
  };
  participants.forEach(add);
  add(localParticipant);
  cameraTracks.forEach((trackRef) => add(trackRef.participant));

  const ordered = [...people.values()].sort((a, b) => {
    if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1;
    return (a.joinedAt?.getTime() ?? 0) - (b.joinedAt?.getTime() ?? 0);
  });

  return ordered.map((participant) => {
    const matches = cameraTracks.filter(
      (trackRef) => trackRef.participant.identity === participant.identity,
    );
    const withMedia = matches.find(
      (trackRef) => isTrackReference(trackRef) && Boolean(trackRef.publication?.track),
    );
    return withMedia ?? matches[0] ?? { participant, source: Track.Source.Camera };
  });
}

function usePreflightSupervisorMic() {
  const { t } = useTranslation();
  const { localParticipant } = useLocalParticipant();
  const [micError, setMicError] = useState<string | null>(null);
  const [micMuted, setMicMuted] = useState(true);

  const micFailedCopy = t('oralExam.lecturer.preflightReview.supervisors.micFailed');

  useEffect(() => {
    let cancelled = false;
    localParticipant.setMicrophoneEnabled(false)
      .then(() => {
        if (!cancelled) {
          setMicMuted(true);
          setMicError(null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMicMuted(true);
          setMicError(micFailedCopy);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [localParticipant, micFailedCopy]);

  const handleToggleMic = useCallback(async (enabled: boolean) => {
    try {
      await localParticipant.setMicrophoneEnabled(enabled);
      setMicError(null);
      setMicMuted(!enabled);
    } catch {
      setMicMuted(true);
      setMicError(micFailedCopy);
    }
  }, [localParticipant, micFailedCopy]);

  return { micMuted, micError, handleToggleMic };
}

function PreflightSupervisorLiveTiles({
  localCameraVideoRef,
  onLocalCameraVideoReady,
  micMuted,
  micError,
  onMicToggle,
  linkCopied,
  copyError,
  sittingLink,
  onCopyLink,
}: {
  localCameraVideoRef?: RefObject<HTMLVideoElement | null>;
  onLocalCameraVideoReady?: () => void;
  micMuted: boolean;
  micError: string | null;
  onMicToggle: (enabled: boolean) => void;
  linkCopied: boolean;
  copyError: boolean;
  sittingLink: string;
  onCopyLink: () => void;
}) {
  const { localParticipant } = useLocalParticipant();
  const participants = useParticipants();
  const cameraTracks = useTracks(
    [{ source: Track.Source.Camera, withPlaceholder: true }],
    { onlySubscribed: true },
  );
  const lecturerTracks = useMemo(
    () => supervisorCameraTiles(cameraTracks, participants, localParticipant),
    [cameraTracks, localParticipant, participants],
  );

  const remoteTracks = lecturerTracks.filter((trackRef) => !trackRef.participant.isLocal);
  const localTrack = lecturerTracks.find((trackRef) => trackRef.participant.isLocal);
  const hasColleagues = remoteTracks.length > 0;

  const tiles = (
    <PreflightSupervisorTiles
      colleagues={remoteTracks.map((trackRef) => ({
        identity: trackRef.participant.identity,
        node: <ParticipantTile trackRef={trackRef} rounded="lg" />,
      }))}
      self={localTrack ? {
        identity: localTrack.participant.identity,
        node: (
          <ParticipantTile
            trackRef={localTrack}
            rounded={hasColleagues ? 'md' : 'lg'}
            showNameBadge={!hasColleagues}
          />
        ),
      } : null}
    />
  );

  return (
    <>
      {localCameraVideoRef ? (
        <PreflightLocalCameraCapture
          captureVideoRef={localCameraVideoRef}
          onCaptureVideoReady={onLocalCameraVideoReady}
        />
      ) : null}
      <PreflightSupervisorsColumnLayout
        hereCount={lecturerTracks.length}
        tiles={tiles}
        isEmpty={lecturerTracks.length === 0}
        micMuted={micMuted}
        micError={micError}
        onMicToggle={onMicToggle}
        linkCopied={linkCopied}
        copyError={copyError}
        sittingLink={sittingLink}
        onCopyLink={onCopyLink}
      />
    </>
  );
}

function PreflightSupervisorsLabTiles({
  selfViewImageUrl,
  colleagueStandIn,
  linkCopied,
  copyError,
  sittingLink,
  onCopyLink,
}: {
  selfViewImageUrl: NonNullable<ReturnType<typeof useOralExamLabOverrides>>['selfViewImageUrl'];
  colleagueStandIn: NonNullable<ReturnType<typeof useOralExamLabOverrides>>['preflightColleagueStandIn'];
  linkCopied: boolean;
  copyError: boolean;
  sittingLink: string;
  onCopyLink: () => void;
}) {
  const hasColleague = Boolean(colleagueStandIn);
  const hereCount = hasColleague ? 2 : 1;

  const tiles = (
    <PreflightSupervisorTiles
      colleagues={colleagueStandIn ? [{
        identity: colleagueStandIn.identity,
        node: (
          <LabStandInImage
            source={colleagueStandIn.imageUrl}
            testID="patrol-lab-standin-preflight-colleague"
          />
        ),
      }] : []}
      self={{
        identity: 'preflight-lab-self',
        node: (
          <LabStandInImage
            source={selfViewImageUrl!}
            testID="patrol-lab-standin-preflight-self-view"
          />
        ),
      }}
    />
  );

  return (
    <PreflightSupervisorsColumnLayout
      hereCount={hereCount}
      tiles={tiles}
      micMuted
      micError={null}
      onMicToggle={() => undefined}
      linkCopied={linkCopied}
      copyError={copyError}
      sittingLink={sittingLink}
      onCopyLink={onCopyLink}
    />
  );
}

/** Mic hook must run inside MeetingRoom where LiveKit context exists. */
function PreflightSupervisorLiveTilesWithMic({
  localCameraVideoRef,
  onLocalCameraVideoReady,
  linkCopied,
  copyError,
  sittingLink,
  onCopyLink,
}: {
  localCameraVideoRef?: RefObject<HTMLVideoElement | null>;
  onLocalCameraVideoReady?: () => void;
  linkCopied: boolean;
  copyError: boolean;
  sittingLink: string;
  onCopyLink: () => void;
}) {
  const { micMuted, micError, handleToggleMic } = usePreflightSupervisorMic();

  return (
    <PreflightSupervisorLiveTiles
      localCameraVideoRef={localCameraVideoRef}
      onLocalCameraVideoReady={onLocalCameraVideoReady}
      micMuted={micMuted}
      micError={micError}
      onMicToggle={(enabled) => { handleToggleMic(enabled).catch(() => undefined); }}
      linkCopied={linkCopied}
      copyError={copyError}
      sittingLink={sittingLink}
      onCopyLink={onCopyLink}
    />
  );
}

export function PreflightSupervisorsColumn({
  slotId,
  joinSettings,
  joinCreds,
  onRoomTokenConnected,
  localCameraVideoRef,
  onLocalCameraVideoReady,
}: PreflightSupervisorsColumnProps) {
  const lab = useOralExamLabOverrides();
  const labSelfView = lab?.selfViewImageUrl ?? null;
  const labColleague = lab?.preflightColleagueStandIn ?? null;
  const [copyError, setCopyError] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const sittingLink = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return window.location.href;
  }, []);

  const handleCopyLink = useCallback(async () => {
    setCopyError(false);
    setLinkCopied(false);
    try {
      await Clipboard.setStringAsync(sittingLink);
      setLinkCopied(true);
    } catch {
      setCopyError(true);
    }
  }, [sittingLink]);

  const onCopyLinkPress = useCallback(() => {
    handleCopyLink().catch(() => undefined);
  }, [handleCopyLink]);

  if (labSelfView) {
    return (
      <PreflightSupervisorsLabTiles
        selfViewImageUrl={labSelfView}
        colleagueStandIn={labColleague}
        linkCopied={linkCopied}
        copyError={copyError}
        sittingLink={sittingLink}
        onCopyLink={onCopyLinkPress}
      />
    );
  }

  if (!joinCreds?.token || !joinCreds.livekitUrl) {
    return (
      <PreflightSupervisorsColumnLayout
        hereCount={0}
        loading
        tiles={null}
        micMuted
        micError={null}
        onMicToggle={() => undefined}
        linkCopied={false}
        copyError={false}
        sittingLink={sittingLink}
        onCopyLink={onCopyLinkPress}
      />
    );
  }

  const supervisorJoinSettings: MeetingJoinSettings = {
    ...joinSettings,
    cameraEnabled: true,
    microphoneEnabled: false,
  };

  return (
    <MeetingRoom
      key={joinCreds.roomName || `preflight-supervisors-${slotId}`}
      token={joinCreds.token}
      serverUrl={joinCreds.livekitUrl}
      participantRole="lecturer"
      observabilitySurface="preflight"
      joinSettings={supervisorJoinSettings}
      onConnected={onRoomTokenConnected}
    >
      <PreflightSupervisorLiveTilesWithMic
        localCameraVideoRef={localCameraVideoRef}
        onLocalCameraVideoReady={onLocalCameraVideoReady}
        linkCopied={linkCopied}
        copyError={copyError}
        sittingLink={sittingLink}
        onCopyLink={onCopyLinkPress}
      />
    </MeetingRoom>
  );
}
